// Top-up Database Operations
// Handles CRUD operations for top-up catalog and purchases

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { appDb } from '../appFirebaseClient';

// Collection references
const TOPUP_CATALOG_COLLECTION = 'topup_catalog';
const TOPUP_PURCHASES_COLLECTION = 'topup_purchases';

/**
 * Get all active top-ups from catalog
 * @returns {Promise<Array>} Array of top-up objects
 */
export const getAllTopups = async () => {
  try {
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const q = query(topupsRef, where('isActive', '==', true), orderBy('displayOrder', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching top-ups:', error);
    throw error;
  }
};

/**
 * Get all top-ups (including inactive) for admin management
 * @returns {Promise<Array>} Array of all top-up objects
 */
export const getAllTopupsForAdmin = async () => {
  try {
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const q = query(topupsRef, orderBy('displayOrder', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching all top-ups:', error);
    throw error;
  }
};

/**
 * Get top-up by ID
 * @param {string} topupId - Top-up ID
 * @returns {Promise<Object|null>} Top-up object or null
 */
export const getTopupById = async (topupId) => {
  try {
    const topupRef = doc(appDb, TOPUP_CATALOG_COLLECTION, topupId);
    const topupSnap = await getDoc(topupRef);
    
    if (topupSnap.exists()) {
      return {
        id: topupSnap.id,
        ...topupSnap.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching top-up by ID:', error);
    throw error;
  }
};

/**
 * Get top-ups by category
 * @param {string} category - Category name
 * @returns {Promise<Array>} Array of top-up objects
 */
export const getTopupsByCategory = async (category) => {
  try {
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const q = query(
      topupsRef, 
      where('category', '==', category),
      where('isActive', '==', true),
      orderBy('displayOrder', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching top-ups by category:', error);
    throw error;
  }
};

/**
 * Get popular top-ups
 * @returns {Promise<Array>} Array of popular top-up objects
 */
export const getPopularTopups = async () => {
  try {
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const q = query(
      topupsRef, 
      where('popular', '==', true),
      where('isActive', '==', true),
      orderBy('displayOrder', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching popular top-ups:', error);
    throw error;
  }
};

/**
 * Get the next available display order number
 * @returns {Promise<number>} Next available order number
 */
export const getNextDisplayOrder = async () => {
  try {
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const q = query(topupsRef, orderBy('displayOrder', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return 1; // First top-up gets order 1
    }
    
    const lastTopup = querySnapshot.docs[0].data();
    return (lastTopup.displayOrder || 0) + 1;
  } catch (error) {
    console.error('Error getting next display order:', error);
    return 1; // Fallback to 1 if there's an error
  }
};

/**
 * Create new top-up
 * @param {Object} topupData - Top-up data
 * @returns {Promise<string>} New top-up ID
 */
export const createTopup = async (topupData) => {
  try {
    // Validate required fields
    if (!topupData.name || !topupData.price || !topupData.limits) {
      throw new Error('Missing required fields: name, price, limits');
    }
    
    // Get next available order number if not specified
    const displayOrder = topupData.displayOrder || await getNextDisplayOrder();
    
    // Set default values
    const newTopup = {
      ...topupData,
      isActive: topupData.isActive !== undefined ? topupData.isActive : true,
      displayOrder: displayOrder,
      popular: topupData.popular || false,
      currency: topupData.currency || '₹',
      category: topupData.category || 'general',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const docRef = await addDoc(topupsRef, newTopup);
    
    console.log('✅ Top-up created successfully:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error creating top-up:', error);
    throw error;
  }
};

/**
 * Update existing top-up
 * @param {string} topupId - Top-up ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<void>}
 */
export const updateTopup = async (topupId, updateData) => {
  try {
    const topupRef = doc(appDb, TOPUP_CATALOG_COLLECTION, topupId);
    
    const updatePayload = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(topupRef, updatePayload);
    console.log('✅ Top-up updated successfully:', topupId);
  } catch (error) {
    console.error('Error updating top-up:', error);
    throw error;
  }
};

/**
 * Delete top-up (soft delete by setting isActive to false)
 * @param {string} topupId - Top-up ID
 * @returns {Promise<void>}
 */
export const deleteTopup = async (topupId) => {
  try {
    const topupRef = doc(appDb, TOPUP_CATALOG_COLLECTION, topupId);
    
    await updateDoc(topupRef, {
      isActive: false,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Top-up deleted successfully:', topupId);
  } catch (error) {
    console.error('Error deleting top-up:', error);
    throw error;
  }
};

/**
 * Hard delete top-up (permanent removal)
 * @param {string} topupId - Top-up ID
 * @returns {Promise<void>}
 */
export const hardDeleteTopup = async (topupId) => {
  try {
    const topupRef = doc(appDb, TOPUP_CATALOG_COLLECTION, topupId);
    await deleteDoc(topupRef);
    console.log('✅ Top-up permanently deleted:', topupId);
  } catch (error) {
    console.error('Error permanently deleting top-up:', error);
    throw error;
  }
};

/**
 * Get top-up categories
 * @returns {Promise<Array>} Array of unique categories
 */
export const getTopupCategories = async () => {
  try {
    const topups = await getAllTopupsForAdmin();
    const categories = [...new Set(topups.map(topup => topup.category))];
    return categories.sort();
  } catch (error) {
    console.error('Error fetching top-up categories:', error);
    throw error;
  }
};

/**
 * Reorder top-ups
 * @param {Array} topupOrders - Array of {topupId, displayOrder} objects
 * @returns {Promise<void>}
 */
export const reorderTopups = async (topupOrders) => {
  try {
    const updatePromises = topupOrders.map(({ topupId, displayOrder }) => {
      const topupRef = doc(appDb, TOPUP_CATALOG_COLLECTION, topupId);
      return updateDoc(topupRef, {
        displayOrder,
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
    console.log('✅ Top-ups reordered successfully');
  } catch (error) {
    console.error('Error reordering top-ups:', error);
    throw error;
  }
};

/**
 * Get top-up purchase history for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Array of purchase records
 */
export const getTopupPurchaseHistory = async (companyId) => {
  try {
    const purchasesRef = collection(appDb, TOPUP_PURCHASES_COLLECTION);
    const q = query(
      purchasesRef,
      where('companyId', '==', companyId),
      orderBy('purchasedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const purchases = [];
    querySnapshot.forEach((doc) => {
      purchases.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return purchases;
  } catch (error) {
    console.error('Error fetching top-up purchase history:', error);
    throw error;
  }
};

/**
 * Record top-up purchase
 * @param {Object} purchaseData - Purchase data
 * @returns {Promise<string>} Purchase record ID
 */
export const recordTopupPurchase = async (purchaseData) => {
  try {
    const purchase = {
      ...purchaseData,
      purchasedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };
    
    const purchasesRef = collection(appDb, TOPUP_PURCHASES_COLLECTION);
    const docRef = await addDoc(purchasesRef, purchase);
    
    console.log('✅ Top-up purchase recorded:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error recording top-up purchase:', error);
    throw error;
  }
};

/**
 * Get top-up analytics
 * @returns {Promise<Object>} Analytics data
 */
export const getTopupAnalytics = async () => {
  try {
    // Get top-ups data
    const topups = await getAllTopupsForAdmin();
    
    // Analytics removed - can be retrieved from payment logs later
    let purchaseData = [];
    
    // Calculate analytics
    const analytics = {
      totalTopups: topups.length,
      activeTopups: topups.filter(t => t.isActive).length,
      totalPurchases: purchaseData.length,
      totalRevenue: purchaseData.reduce((sum, p) => sum + (p.amount || 0), 0),
      topSellingTopups: {},
      categoryStats: {},
      monthlyStats: {}
    };
    
    // Top selling top-ups
    purchaseData.forEach(purchase => {
      const topupId = purchase.topupId;
      if (!analytics.topSellingTopups[topupId]) {
        analytics.topSellingTopups[topupId] = { count: 0, revenue: 0 };
      }
      analytics.topSellingTopups[topupId].count++;
      analytics.topSellingTopups[topupId].revenue += purchase.amount || 0;
    });
    
    // Category stats
    topups.forEach(topup => {
      const category = topup.category;
      if (!analytics.categoryStats[category]) {
        analytics.categoryStats[category] = { count: 0, active: 0 };
      }
      analytics.categoryStats[category].count++;
      if (topup.isActive) {
        analytics.categoryStats[category].active++;
      }
    });
    
    return analytics;
  } catch (error) {
    console.error('Error fetching top-up analytics:', error);
    // Return basic analytics even if there's an error
    return {
      totalTopups: 0,
      activeTopups: 0,
      totalPurchases: 0,
      totalRevenue: 0,
      topSellingTopups: {},
      categoryStats: {},
      monthlyStats: {}
    };
  }
};

/**
 * Fix duplicate display orders by reassigning unique order numbers
 * @returns {Promise<void>}
 */
export const fixDuplicateDisplayOrders = async () => {
  try {
    const topupsRef = collection(appDb, TOPUP_CATALOG_COLLECTION);
    const q = query(topupsRef, orderBy('displayOrder', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Reassign order numbers sequentially
    const updatePromises = topups.map((topup, index) => {
      const newOrder = index + 1;
      if (topup.displayOrder !== newOrder) {
        const topupRef = doc(appDb, TOPUP_CATALOG_COLLECTION, topup.id);
        return updateDoc(topupRef, {
          displayOrder: newOrder,
          updatedAt: serverTimestamp()
        });
      }
      return Promise.resolve();
    });
    
    await Promise.all(updatePromises);
    console.log('✅ Fixed duplicate display orders');
  } catch (error) {
    console.error('Error fixing duplicate display orders:', error);
    throw error;
  }
};

// Export all functions
export default {
  getAllTopups,
  getAllTopupsForAdmin,
  getTopupById,
  getTopupsByCategory,
  getPopularTopups,
  createTopup,
  updateTopup,
  deleteTopup,
  hardDeleteTopup,
  getTopupCategories,
  reorderTopups,
  getTopupPurchaseHistory,
  recordTopupPurchase,
  getTopupAnalytics,
  getNextDisplayOrder,
  fixDuplicateDisplayOrders
};

